/**
 * Connector test panel component.
 * Provides UI for testing connector connections.
 */

// React Imports
import { useState } from 'react';
import { Button } from '@coordio/ui';
import {
  Play as PlayArrowIcon,
  CheckCircle as CheckCircleIcon,
  CircleAlert as ErrorIcon,
} from 'lucide-react';
import { connectorService } from '../../services/connectorService';
import { useNotificationStore } from '../../store/notificationStore';
import type { TestConnectionResponse } from '../../types/connector';

export interface ConnectorTestPanelProps {
  instanceId: string;
  connectorName: string;
  onTestComplete?: (result: TestConnectionResponse) => void;
}

/**
 * Connector test panel component.
 */
export const ConnectorTestPanel = ({
  instanceId,
  connectorName,
  onTestComplete,
}: ConnectorTestPanelProps) => {
  const { addNotification } = useNotificationStore();
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestConnectionResponse | null>(null);

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await connectorService.testConnection(instanceId);
      setTestResult(result);
      onTestComplete?.(result);

      if (result.success) {
        addNotification(`Connection test successful for ${connectorName}`, 'success');
      } else {
        addNotification(`Connection test failed: ${result.message}`, 'error');
      }
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'Connection test failed';
      setTestResult({
        success: false,
        message,
      });
      addNotification(message, 'error');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h6 className="text-lg font-semibold">
            Test Connection
          </h6>
          <Button
            onClick={handleTest}
            disabled={testing}
          >
            {testing ? (
              <span className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
            ) : (
              <PlayArrowIcon size={16} className="mr-2" />
            )}
            {testing ? 'Testing...' : 'Test Connection'}
          </Button>
        </div>

        {testResult && (
          <>
            <hr className="my-4" />
            {testResult.success ? (
              <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-800 dark:border-green-800/40 dark:bg-green-950/50 dark:text-green-300 mb-4">
                <div className="flex items-center gap-2">
                  <CheckCircleIcon size={16} />
                  <span>Connection test successful!</span>
                </div>
                {testResult.durationMs && (
                  <span className="text-xs block mt-1">
                    Response time: {testResult.durationMs}ms
                  </span>
                )}
              </div>
            ) : (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-800/40 dark:bg-red-950/50 dark:text-red-300 mb-4">
                <div className="flex items-center gap-2">
                  <ErrorIcon size={16} />
                  <span>Connection test failed: {testResult.message}</span>
                </div>
                {testResult.statusCode && (
                  <span className="text-xs block mt-1">
                    Status Code: {testResult.statusCode}
                  </span>
                )}
              </div>
            )}
          </>
        )}

        <p className="text-sm text-muted-foreground">
          Test the connection to verify that authentication credentials are valid and the connector can communicate with the external service.
        </p>
      </div>
    </div>
  );
};
